import React from 'react';
import { GenericSelect } from '@/styles/GenericForm';
import { Input } from '@/components/ui/input';
import { phoneCountryCodes, DEFAULT_PHONE_ISO } from '@/lib/phone-country-codes';

/**
 * Generic phone-number field with a country dial-code dropdown. Stores/
 * receives the composed value as a single string ("+1 5551234567"),
 * matching how `users.phone` is already stored -- the split into
 * (country ISO, local number) is purely a UI concern, handled internally.
 * Extracted from Settings > Account's original inline implementation
 * (2026-09-12) so every phone field in the app can share one component
 * instead of re-deriving the same split/compose logic per caller.
 */

function dialCodeFor(iso: string): string {
  return phoneCountryCodes.find((c) => c.iso === iso)?.dialCode || '+1';
}

/** Splits a stored "+1 5551234567" phone into (iso, local) for editing. */
function splitPhone(stored: string | null | undefined): { iso: string; local: string } {
  if (!stored) return { iso: DEFAULT_PHONE_ISO, local: '' };
  const trimmed = stored.trim();
  const match = phoneCountryCodes
    .slice()
    .sort((a, b) => b.dialCode.length - a.dialCode.length) // longest dial code first (avoid +1 matching +12x)
    .find((c) => trimmed.startsWith(c.dialCode));
  if (match) {
    return { iso: match.iso, local: trimmed.slice(match.dialCode.length).trim() };
  }
  return { iso: DEFAULT_PHONE_ISO, local: trimmed };
}

interface PhoneCountryInputProps {
  /** Composed phone string, e.g. "+1 5551234567". Empty string/null/undefined for no phone. */
  value: string | null | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
  id?: string;
  className?: string;
  placeholder?: string;
}

export function PhoneCountryInput({ value, onChange, disabled, id, className, placeholder }: PhoneCountryInputProps) {
  const { iso, local } = splitPhone(value);

  const compose = (nextIso: string, nextLocal: string) =>
    nextLocal.trim() ? `${dialCodeFor(nextIso)} ${nextLocal.trim()}` : '';

  // Layout: a fixed, narrow column for the dial code and the rest of the row for the number. The width goes on a WRAPPER on purpose: GenericSelect's trigger
  // carries `.generic-form-field { width: 100% }` (design-system/forms.css), which beats a width utility put on the select itself -- a `w-36` there was
  // ignored, the select took the whole row and the number input (flex-1) was squeezed to almost nothing.
  return (
    <div className={`flex gap-2 ${className || ''}`}>
      <div className="w-28 shrink-0" data-testid="phone-country">
        <GenericSelect
          value={iso}
          onValueChange={(v) => onChange(compose(v, local))}
          disabled={disabled}
          required
          contentClassName="max-h-[18.75rem] overflow-y-auto"
          // Several countries share a dial code (+1), so the trigger shows just
          // the code for the selected ISO instead of the matched item's label.
          renderValue={dialCodeFor(iso)}
          options={phoneCountryCodes.map((c) => ({ label: `${c.name} (${c.dialCode})`, value: c.iso }))}
        />
      </div>
      <div className="min-w-0 flex-1" data-testid="phone-number">
        <Input
          id={id}
          type="tel"
          inputMode="tel"
          placeholder={placeholder || 'Phone number'}
          value={local}
          disabled={disabled}
          onChange={(e) => onChange(compose(iso, e.target.value))}
        />
      </div>
    </div>
  );
}
